import { Board } from "@common-models/board";
import { clientClockFields } from "@common/services/client-clock-fields";
import { Campaign } from "@common-models/campaign-model";
import { TaskColumn } from "@common-models/task-column";
import { TasksGroup } from "@common-models/task-group";
import { AnalyticsService } from "@common-services/analytics-service";
import { ProductEvent, trackProductEvent } from "@common-services/product-analytics";
import { AppThunkDispatch } from "@common/types/common-root-state-type";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { dripsSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { resolveDripStepDrop, shiftRrulesetAnchor, type DripDropRefusal } from "@common-views/calendar/grid/layout/drip-drag";
import { campaignReducerActions, campaignSelectors, campaignsSelectOneObjectByQuery, DBBoardsThunks, DBCampaignThunks, DBDripThunks, DBMessageLogThunks, DBTaskColumnThunks, DBTaskgroupsThunks, DBTasksThunks, DBTemplateThunks, DBUserMessageThunks, DBUserThunks, messageLogReducerActions, taskColumnSelectByQuery, userMessageReducerActions, userMessagesSelectByQuery } from "@common-reducers/DBServiceThunks";
import { dripIdsFromCampaigns } from "@common-reducers/drip-ids";
import { chunkIds } from "@common-reducers/in-query";
import { setUser } from "@common-reducers/UserReducer";
import { resolveWindowToPersist } from "@common-reducers/campaign-send-window-default";
import { CommonRootState } from "@common/types/common-root-state-type";
import moment from "moment";
import randomColor from 'randomcolor';
import { duplicateTemplateForCampaigns, removeAllPendingUserMessagesByCampaignId, backendApi } from "./backend-api/backend-api";
import { boardsActions } from "./BoardsReducer";
import { getCurrentBoard } from "./BoardsSelectors";
import { bulkAudienceSelectBoardThunk } from "./BoardsThunks";
import { BulkDashboardTab, defaultNewCampaign, mergeDeliveryEvidence, setActiveBulkView, setActiveTab, setCampaignIdForLogsView, setCurrentTemplate, setIsCampaignLaunching, setNewCampaign, setPreviousState } from "./CampaignReducer";
import { findEntity } from "@common-reducers/backend-api/backend-api";
import { scheduledMessageEditorActions } from "./ScheduledMessageEditorReducer";
import { getRunningCampaigns } from "./CampaignSelectors";
import { selectUser, selectUserId } from "./UserSelectors";
import { loadUserMessagesAndCampaignsThunk } from "./UserThunks";
import { Template } from "../models/template";
import { MQTTSubscriber } from "@common/noifier-module/MQTTSubscriber";
import { getClientType } from "@common/services/detect-context";
import { appConfig } from "@common/config/app.config";


export interface SaveNewContactsFromWhatsappForNewCampaignAudienceParams {
    displayName: string;
    waContactList: any[];
}
export interface SaveNewContactsFromWhatsappForExistingCampaignAudienceParams {
    waContactList: any[];
}
export interface SaveNewContactsFromWhatsappForExistingCampaignAudienceWithDataMappingParams {
    waContactList: any[];
    importDataMapping: Record<string, string>;
}

// Helper function to split names
const splitName = (contact: any) => {
    let firstName = '';
    let lastName = '';
    if (contact?.name) {
        const nameParts = contact?.name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || ''; // Support for middle and last name
    }
    if (!contact?.name && contact?.verifiedName) {
        const nameParts = contact?.verifiedName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || ''; // Support for middle and last name
    }
    if (contact?.firstName) {
        firstName = contact?.firstName;
    }
    if (contact?.lastName) {
        lastName = contact?.lastName;
    }
    return { firstName, lastName };
};

// Helper function for creating bulk update data
const createBulkUpdateData = (waContactList: any[], ownerId: string, boardId: string, groupId: string, contactColumn: any, firstNameColumn: any, lastNameColumn: any, importDataMapping?: { [key: string]: string }) => {
    return waContactList.map((contact, index) => {
        const { firstName, lastName } = splitName(contact);
        const isGroup = contact?.whatsappId?.includes('@g');
        const contactColumnValue = contact?.whatsappId;
        const displayName = contact?.displayName ?? contact?.name ?? `${firstName} ${lastName}`;

        let customData: Record<string, { value: string }> = {
            [contactColumn?._id]: { value: contactColumnValue },
        };

        if (importDataMapping) {
            if (importDataMapping['First name']) {
                customData[importDataMapping['First name']] = { value: isGroup ? displayName : firstName };
            }
            if (importDataMapping['Last name']) {
                customData[importDataMapping['Last name']] = { value: isGroup ? '' : lastName };
            }
        } else {
            customData[firstNameColumn?._id] = { value: isGroup ? displayName : firstName };
            customData[lastNameColumn?._id] = { value: isGroup ? '' : lastName };
        }

        return {
            owner: ownerId,
            boardId: boardId,
            groupId: groupId,
            text: displayName,
            customData: customData,
            order: index,
        };
    });
};

export const saveNewContactsFromWhatsappForNewCampaignAudienceThunk = createAsyncThunk<string, SaveNewContactsFromWhatsappForNewCampaignAudienceParams, { state: CommonRootState }>(
    "tasks/updateCellValueThunk",
    async (params, thunkAPI) => {
        // Extracted common code for setting up new contacts
        const { displayName, waContactList } = params;
        const state: CommonRootState = thunkAPI.getState();
        const ownerId = selectUserId(state);
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const user = selectUser(state);

        // Unique part for new campaign audience
        const resBoard = await dispatch(DBBoardsThunks.create({
            name: displayName,
            workspaceId: user?.currentWorkspace?.toString() ?? '',
            type: 'audience',
            owner: ownerId,
        }));
        const board: Board = resBoard.payload as Board;

        const resGroup = await dispatch(DBTaskgroupsThunks.create({
            boardId: board._id,
            name: "Audience",
            owner: ownerId,
            createdBy: ownerId,
            colorAccent: randomColor(),
            order: 0,
        }));
        const group: TasksGroup = resGroup.payload as TasksGroup;

        const resContactColumn = await dispatch(DBTaskColumnThunks.create({
            boardId: board._id,
            type: 'whatsapp-id-cell',
            title: 'Contact',
            order: 1,
        }));
        const contactColumn = resContactColumn.payload as TaskColumn;

        const resFirstNameColumn = await dispatch(DBTaskColumnThunks.create({
            boardId: board._id,
            type: 'text-cell',
            title: 'First Name',
            order: 2,
        }));
        const firstNameColumn = resFirstNameColumn.payload as TaskColumn;

        const resLastNameColumn = await dispatch(DBTaskColumnThunks.create({
            boardId: board._id,
            type: 'text-cell',
            title: 'Last Name',
            order: 3,
        }));
        const lastNameColumn = resLastNameColumn.payload as TaskColumn;

        // Use helper function for creating bulk update data
        const bulkUpdateData = createBulkUpdateData(waContactList, ownerId ?? '', board._id ?? '', group._id ?? '', contactColumn, firstNameColumn, lastNameColumn);

        await dispatch(DBTasksThunks.createMany(bulkUpdateData));

        return board._id ?? '';
    }
);

export const saveNewContactsFromWhatsappForExistingCampaignAudienceThunk = createAsyncThunk<string, SaveNewContactsFromWhatsappForExistingCampaignAudienceParams, { state: CommonRootState }>(
    "tasks/updateCellValueThunk",
    async (params, thunkAPI) => {
        const { waContactList } = params;
        const state: CommonRootState = thunkAPI.getState();
        const ownerId = selectUserId(state);
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const board = getCurrentBoard(state);
        if (!board) return '';

        const resGroup = await dispatch(DBTaskgroupsThunks.create({
            boardId: board._id,
            name: "Whatsapp Audience",
            owner: ownerId,
            createdBy: ownerId,
            colorAccent: randomColor(),
            order: 0,
        }));
        const group: TasksGroup = resGroup.payload as TasksGroup;

        const currentBoardColumns = taskColumnSelectByQuery(state.DBTaskColumnReducer, { boardId: board._id });
        const contactColumn = currentBoardColumns.find(column => column.type === 'whatsapp-id-cell');
        const firstNameColumn = currentBoardColumns.find(column => column.title?.toLowerCase().includes('first'));
        const lastNameColumn = currentBoardColumns.find(column => column.title?.toLowerCase().includes('last'));

        // Use helper function for creating bulk update data
        const bulkUpdateData = createBulkUpdateData(waContactList, ownerId ?? '', board._id ?? '', group._id ?? '', contactColumn, firstNameColumn, lastNameColumn);

        await dispatch(DBTasksThunks.createMany(bulkUpdateData));

        return board._id ?? '';
    }
);

export const saveNewContactsFromWhatsappForExistingCampaignAudienceWithDataMappingThunk = createAsyncThunk<string, SaveNewContactsFromWhatsappForExistingCampaignAudienceWithDataMappingParams, { state: CommonRootState }>(
    "tasks/updateCellValueThunk",
    async (params, thunkAPI) => {
        const { waContactList, importDataMapping } = params;
        const state: CommonRootState = thunkAPI.getState();
        const ownerId = selectUserId(state);
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const board = getCurrentBoard(state);
        if (!board) return '';

        const resGroup = await dispatch(DBTaskgroupsThunks.create({
            boardId: board._id,
            name: "Whatsapp Audience",
            owner: ownerId,
            createdBy: ownerId,
            colorAccent: randomColor(),
            order: 0,
        }));
        const group: TasksGroup = resGroup.payload as TasksGroup;

        const currentBoardColumns = taskColumnSelectByQuery(state.DBTaskColumnReducer, { boardId: board._id });
        const contactColumn = currentBoardColumns.find(column => column.type === 'whatsapp-id-cell');

        // Use helper function for creating bulk update data with mapping
        const bulkUpdateData = createBulkUpdateData(waContactList, ownerId ?? '', board._id ?? '', group._id ?? '', contactColumn, null, null, importDataMapping);

        await dispatch(DBTasksThunks.createMany(bulkUpdateData));

        return board._id ?? '';
    }
);



export interface GetBoardEntitiesForImportModalThunkParams {
    displayName: string;
}

export const getBoardEntitiesForImportModalThunk = createAsyncThunk<
    string[],
    GetBoardEntitiesForImportModalThunkParams,
    { state: CommonRootState }
>(
    "campaigns/getBoardEntitiesForImportModal",
    async (getBoardEntitiesForImportModalThunkParams: GetBoardEntitiesForImportModalThunkParams, thunkAPI) => {
        const { displayName } = getBoardEntitiesForImportModalThunkParams;
        const state: CommonRootState = thunkAPI.getState();
        const ownerId = selectUserId(state)
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const user = selectUser(state)

        const board = await dispatch(DBBoardsThunks.create(
            {
                name: displayName,
                workspaceId: user?.currentWorkspace?.toString() ?? '',
                type: 'audience',
                owner: ownerId,
            })).unwrap()


        const firstNameColumn = await dispatch(DBTaskColumnThunks.create({
            boardId: board._id,
            type: 'text-cell',
            title: 'First Name',
        })).unwrap()


        const lastNameColumn = await dispatch(DBTaskColumnThunks.create({
            boardId: board._id,
            type: 'text-cell',
            title: 'Last Name',
        })).unwrap()

        const contactColumn = await dispatch(DBTaskColumnThunks.create({
            boardId: board._id,
            type: 'whatsapp-id-cell',
            title: 'Contact',
        })).unwrap()



        return [board._id?.toString() ?? '', firstNameColumn._id?.toString() ?? '', lastNameColumn._id?.toString() ?? '', contactColumn._id?.toString() ?? '']
    }
);

export interface DuplicateTemplateForCampaignsThunk {
    oldTemplateId: string;
}

export const duplicateTemplateForCampaignsThunk = createAsyncThunk<
    any,
    DuplicateTemplateForCampaignsThunk,
    { state: CommonRootState }
>(
    "tasks/updateCellValueThunk",
    async (DuplicateTemplateForCampaignsThunk: DuplicateTemplateForCampaignsThunk, thunkAPI) => {
        const { oldTemplateId } = DuplicateTemplateForCampaignsThunk;
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        await dispatch(duplicateTemplateForCampaigns.initiate(oldTemplateId))

    }
);


export interface CreateNewColumnsForImportExcelThunkParams {
    boardId: string;
}

export const createNewColumnsForImportExcelThunk = createAsyncThunk<
    any,
    CreateNewColumnsForImportExcelThunkParams,
    { state: CommonRootState }
>(
    "tasks/updateCellValueThunk",
    async (CreateNewColumnsForImportExcelThunkParams: CreateNewColumnsForImportExcelThunkParams, thunkAPI) => {
        const { boardId } = CreateNewColumnsForImportExcelThunkParams;
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state: CommonRootState = thunkAPI.getState();
        const importDataMapping = state.ImportItemsReducer.importDataMapping
        const newColumnList = state.ImportItemsReducer.newColumnList
        /// return all the elements in newColumnList that their _id is a value in importDataMapping
        const _columnsToCreate = (newColumnList ?? []).filter(column => column._id !== undefined && Object.values(importDataMapping).includes(column._id))
        // map columnsToCreate to the same array just add to each element the field 'excelColumn' with the key of the importDataMapping that has the value of the column._id
        const columnsToCreate = _columnsToCreate.map(column => ({
            ...column,
            excelColumn: Object.keys(importDataMapping).find(key => importDataMapping[key] === column._id)
        }))

        if (columnsToCreate.length === 0) {
            return undefined
        } else {
            let newColumnsMapping: any = {}
            for (let newColumnToCreate of columnsToCreate) {
                const column = await dispatch(DBTaskColumnThunks.create({
                    boardId,
                    type: newColumnToCreate.type,
                    title: newColumnToCreate.title,
                })).unwrap();
                if (newColumnToCreate._id === undefined) continue;
                newColumnsMapping[newColumnToCreate._id] = {
                    _id: column._id,
                    excelColumn: newColumnToCreate.excelColumn
                }
            }
            return newColumnsMapping
        }

    }
);


export interface ToggleAudienceCheckboxParams {
    boardId: string;
}

export const toggleAudienceCheckboxThunk = createAsyncThunk<
    void,
    ToggleAudienceCheckboxParams,
    { state: CommonRootState }
>(
    "campaigns/toggleAudienceCheckboxThunk",
    async ({ boardId }: ToggleAudienceCheckboxParams, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state: CommonRootState = thunkAPI.getState();
        const newCampaign = state.CampaignReducer.newCampaign;

        // Determine if the boardId is already in the array
        const isBoardIdPresent = newCampaign?.boardIDArray?.includes(boardId);

        let newBoardIdArray: string[] = [];

        if (isBoardIdPresent) {
            // Remove the boardId from the array
            newBoardIdArray = newCampaign?.boardIDArray?.filter(id => id !== boardId) ?? [];
        } else {
            // Add the boardId to the array
            newBoardIdArray = [...(newCampaign?.boardIDArray ?? []), boardId];
        }

        // Dispatch the new state
        dispatch(setNewCampaign({
            ...newCampaign,
            boardIDArray: newBoardIdArray,
        }));
    }
);



export interface HandleActiveBulkViewNavigationParams {
    navigate: "toNewAudience"
    | "toCampaignBoardView"
    | "fromCampaignBoardView"
    | "toNewTemplate"
    | "fromNewAudience"
    | "fromNewTemplate";
    boardId?: string;
}

export const handleActiveBulkViewNavigationThunk = createAsyncThunk<
    void,
    HandleActiveBulkViewNavigationParams,
    { state: CommonRootState }
>(
    "campaigns/handleActiveBulkViewNavigationThunk",
    async (HandleActiveBulkViewNavigationParams: HandleActiveBulkViewNavigationParams, thunkAPI) => {
        const { navigate, boardId } = HandleActiveBulkViewNavigationParams
        const analyticsService: AnalyticsService = new AnalyticsService();

        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state: CommonRootState = thunkAPI.getState();
        const activeBulkView = state.CampaignReducer.activeBulkView

        if (navigate === "toNewAudience") {
            analyticsService.event('bulk_dashboard_tab_change', {
                category: 'bulk',
                action: 'newAudience'
            });

            // Save current state before navigating
            dispatch(setPreviousState({
                activeTab: state.CampaignReducer.activeTab,
                activeBulkView: activeBulkView
            }));

            if (activeBulkView === "campaignManagementView") {
                dispatch(setActiveTab('audiences'))
                dispatch(setActiveBulkView("campaignNewAudienceFromManagementView"))
            }
            else {
                dispatch(setActiveBulkView("campaignNewAudienceFromNewCampaignView"))
            }
        }

        if (navigate === "fromNewAudience") {
            const previousState = state.CampaignReducer.previousState;

            // Restore previous state if available, otherwise fallback to default logic
            if (previousState) {
                dispatch(setActiveTab(previousState.activeTab));
                dispatch(setActiveBulkView(previousState.activeBulkView));
                dispatch(setPreviousState(null));
            } else {
                // Fallback logic (existing behavior)
                if (activeBulkView === "campaignNewAudienceFromManagementView") {
                    dispatch(setActiveBulkView("campaignManagementView"))
                }
                else {
                    dispatch(setActiveBulkView("newCampaignView"))
                }
            }
        }

        if (navigate === "toCampaignBoardView") {
            if (activeBulkView === "campaignManagementView") {
                dispatch(setActiveBulkView("campaignBoardFromManagementView"))
            }
            else {
                dispatch(setActiveBulkView("campaignBoardFromNewCampaignView"))
            }
            if (boardId !== undefined) dispatch(bulkAudienceSelectBoardThunk({ boardId }))
        }

        if (navigate === "fromCampaignBoardView") {
            await dispatch(boardsActions.privateSetSelectedBoardId(''))
            if (activeBulkView === "campaignBoardFromManagementView") {
                dispatch(setActiveBulkView("campaignManagementView"))
            }
            else {
                dispatch(setActiveBulkView("newCampaignView"))
            }
        }

        if (navigate === "toNewTemplate") {
            if (activeBulkView === "campaignManagementView") {
                dispatch(setActiveBulkView("campaignNewTemplateViewFromManagement"))
            }
            else {
                dispatch(setActiveBulkView("campaignNewTemplateViewFromNewCampaignView"))
            }
        }
        if (navigate === "fromNewTemplate") {
            if (activeBulkView === "campaignNewTemplateViewFromManagement") {
                dispatch(setActiveBulkView("campaignManagementView"))
            }
            else {
                dispatch(setActiveTab('audiences'))
                dispatch(setActiveBulkView("newCampaignView"))
            }
        }
    }
);


export interface HandleTimeAndDateTabOnChangeEventParams {
    changeType: string;
    changeObject: any;
}

export const handleTimeAndDateTabOnChangeEventThunk = createAsyncThunk<
    void,
    HandleTimeAndDateTabOnChangeEventParams,
    { state: CommonRootState }
>(
    "campaigns/handleTimeAndDateTabOnChangeEventThunk",
    async (HandleTimeAndDateTabOnChangeEventParams: HandleTimeAndDateTabOnChangeEventParams, thunkAPI) => {
        const { changeType, changeObject } = HandleTimeAndDateTabOnChangeEventParams
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state: CommonRootState = thunkAPI.getState();
        const newCampaign = state.CampaignReducer.newCampaign



        if (changeType === 'setScheduleDueDate') {
            dispatch(setNewCampaign({
                ...newCampaign,
                schedule: {
                    ...newCampaign?.schedule,
                    dueDate: changeObject,
                }
            }));
        }
        if (changeType === 'setScheduleDueDateToNow') {
            dispatch(setNewCampaign({
                ...newCampaign,
                schedule: {
                    ...newCampaign?.schedule,
                    dueDate: changeObject,
                    isNow: true,
                }
            }));
        }
        if (changeType === 'setScheduleDueDateToNotNow') {
            dispatch(setNewCampaign({
                ...newCampaign,
                schedule: {
                    ...newCampaign?.schedule,
                    ...changeObject,
                }
            }));
        }
        if (changeType === 'setNewCampaignTiming') {
            dispatch(setNewCampaign({
                ...newCampaign,
                timing: {
                    messageInterval: newCampaign?.timing?.messageInterval ?? 0,
                    ...newCampaign?.timing,
                    ...changeObject,
                }
            }));
        }
        if (changeType === 'setNewCampaignTimingAutomaticTiming') {
            dispatch(setNewCampaign({
                ...newCampaign,
                timing: {
                    messageInterval: newCampaign?.timing?.messageInterval ?? 0,
                    ...newCampaign?.timing,
                    automaticTiming: {
                        ...newCampaign?.timing?.automaticTiming,
                        ...changeObject,
                    }
                }
            }))
        }
        if (changeType === 'setNewCampaignTimingSendInterval') {
            dispatch(setNewCampaign({
                ...newCampaign,
                timing: {
                    messageInterval: newCampaign?.timing?.messageInterval ?? 0,
                    ...newCampaign?.timing,
                    sendInterval: {
                        ...newCampaign?.timing?.sendInterval,
                        ...changeObject,
                    }
                }
            }));
        }
    }
);

export const launchCampaignThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("campaigns/launchCampaignThunk",
    async (voidParams, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state: CommonRootState = thunkAPI.getState();

        const newCampaign = state.CampaignReducer.newCampaign;

        dispatch(setIsCampaignLaunching(true))

        // Track campaign launch with new centralized analytics
        trackProductEvent(ProductEvent.LaunchCampaign, {
            category: "campaign",
            action: "launch_campaign",
            campaign_name: newCampaign?.name,
            board_count: newCampaign?.boardIDArray?.length || 0,
            has_timing: !!newCampaign?.timing,
            template_type: newCampaign?.template?.templateType,
        });

        // Track first action event
        const analyticsService = new AnalyticsService();
        analyticsService.event("first_action", {
            category: "firstAction",
            action: "first action",
        });


        // Get client information for campaign creation tracking
        let clientId: string | undefined = undefined;
        let clientType: 'gateway' | 'whatsapp' | 'frontend' | undefined = undefined;
        try {
            const mqttSubscriber = MQTTSubscriber.getInstance();
            clientId = mqttSubscriber.getCurrentClientId() || undefined;
            const detectedClientType = mqttSubscriber.getCurrentClientType() || getClientType();
            clientType = detectedClientType ? (detectedClientType as 'gateway' | 'whatsapp' | 'frontend') : undefined;
        } catch (error) {
            // MQTT not available, try to get client type directly
            try {
                const detectedClientType = getClientType();
                clientType = detectedClientType ? (detectedClientType as 'gateway' | 'whatsapp' | 'frontend') : undefined;
            } catch {
                // Ignore errors
            }
        }

        const launchedCampaign: Campaign = {
            ...newCampaign,
            template: {
                ...newCampaign?.template,
                message: newCampaign?.template?.asset?.type?.includes('audio') ? '' : newCampaign?.template?.message ?? "",
            } as Template,
            campaignStatus: 'pending',
            schedule: {
                ...newCampaign?.schedule,
                dueDate: newCampaign?.schedule?.isNow ? moment.utc().toISOString() : newCampaign?.schedule?.dueDate
            },
            timing: {
                messageInterval: newCampaign?.timing?.messageInterval ?? 0,
                ...newCampaign?.timing,
                startTime: {
                    hour: moment().set({ hour: newCampaign?.timing?.startTime?.hour ?? 0 }).utc().hour(),
                    minute: moment().set({ minute: newCampaign?.timing?.startTime?.minute ?? 0 }).utc().minute(),
                },
                endTime: {
                    hour: moment().set({ hour: newCampaign?.timing?.endTime?.hour ?? 23 }).utc().hour(),
                    minute: moment().set({ minute: newCampaign?.timing?.endTime?.minute ?? 59 }).utc().minute(),
                }
            },
            clientId,
            clientType
        }


        dispatch(setNewCampaign(defaultNewCampaign));

        // Check if this is an update (has _id) or a new campaign
        const isUpdate = !!launchedCampaign._id;
        let campaignId: string;

        if (isUpdate) {
            // Update existing campaign
            await dispatch(DBCampaignThunks.patch({
                entity: launchedCampaign
            }));
            campaignId = launchedCampaign._id!;
        } else {
            // Create new campaign
            const res = await dispatch(DBCampaignThunks.create(launchedCampaign));
            campaignId = (res.payload as Campaign)._id!;

            trackProductEvent(ProductEvent.CreateCampaign, {
                campaign_type: newCampaign?.template?.templateType || 'message',
                template_used: !!newCampaign?.template,
            });

        }

        // #max-send-window: remember the launched window choice as the user's cross-surface
        // default (users.preferences.defaultMaxSendWindowMinutes). Remember-last-choice: setting
        // it persists the number; launching with it unchecked persists null (default off).
        // Best-effort — never blocks/fails the launch.
        try {
            const user = state.UserReducer.user;
            const { changed, value } = resolveWindowToPersist(
                launchedCampaign.timing?.maxSendWindowMinutes,
                user?.preferences?.defaultMaxSendWindowMinutes,
            );
            if (user?._id && changed) {
                const res: any = await dispatch(DBUserThunks.patch({
                    entity: {
                        _id: user._id,
                        preferences: { ...user.preferences, defaultMaxSendWindowMinutes: value },
                    } as any,
                }));
                if (res?.payload) dispatch(setUser(res.payload));
            }
        } catch (e) {
            console.warn('max-send-window default persist failed (non-blocking)', e);
        }

        dispatch(setActiveTab("manageCampaigns" as BulkDashboardTab))
        dispatch(setCampaignIdForLogsView(campaignId))
        dispatch(setActiveBulkView("campaignManagementView"))

        dispatch(setIsCampaignLaunching(false))
    }
);


export interface SendAllPendingCampaignMessagesThunkParams {
    campaignId: string;
}

export const sendAllPendingCampaignMessagesThunk = createAsyncThunk<
    void,
    SendAllPendingCampaignMessagesThunkParams,
    { state: CommonRootState }
>(
    "campaigns/SendAllPendingCampaignMessagesThunkThunk",
    async (SendAllPendingCampaignMessagesThunkParams: SendAllPendingCampaignMessagesThunkParams, thunkAPI) => {
        const { campaignId } = SendAllPendingCampaignMessagesThunkParams
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state: CommonRootState = thunkAPI.getState();

        const pendingMessages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            campaignId,
            status: 'pending'
        })

        const nowIsoString = moment().toISOString();

        const changes = pendingMessages.map((message, index) => {
            return {
                id: message._id,
                changes: {
                    dueDate: nowIsoString,
                    dueTime: nowIsoString,
                    // FE#447: "now" is this machine's clock — tell the server so it can record the creator skew.
                    ...clientClockFields(),
                }
            }
        })
        dispatch(DBUserMessageThunks.patchMany(changes))

    }
)











export interface ClearAllPendingCampaignMessagesThunk {
    campaignId: string;
}

export const clearAllPendingCampaignMessagesThunk = createAsyncThunk<
    void,
    ClearAllPendingCampaignMessagesThunk,
    { state: CommonRootState }
>(
    "campaigns/SendAllPendingCampaignMessagesThunkThunk",
    async (ClearAllPendingCampaignMessagesThunk: ClearAllPendingCampaignMessagesThunk, thunkAPI) => {
        const { campaignId } = ClearAllPendingCampaignMessagesThunk
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state = thunkAPI.getState();

        dispatch(DBCampaignThunks.patch({
            entity: {
                _id: campaignId,
                campaignStatus: 'aborted',
            }
        }));

        await dispatch(removeAllPendingUserMessagesByCampaignId.initiate(campaignId))

        // Instead of clearing all state, remove only the messages for this campaign
        // This prevents all calendar events from disappearing temporarily
        const campaignMessages = userMessagesSelectByQuery(
            state.DBUserMessageReducer,
            { campaignId }
        );

        // Remove only messages for this campaign
        if (campaignMessages.length > 0) {
            dispatch(userMessageReducerActions.removeMany(
                campaignMessages.map((message) => message._id)
            ));
        }

        // The campaign will be updated to 'aborted' status by the patch above
        // No need to remove it from state - the refetch will update it

        dispatch(cleanMessageLogsFromStateExceptRunningCampaignsThunk())

        // Refetch to get updated data (this will bring back all campaigns and messages)
        dispatch(loadUserMessagesAndCampaignsThunk())

    })


/**
 * Fills the store that the 5s send tick scans (schedule-service.ts
 * `processNextMessageForPendingAndActiveCampaigns`). A campaign that is not in
 * the store is never fired by this client — so what this loads defines what
 * this client can dispatch.
 *
 * TWO queries, because the backend partitions the collection (BE#186).
 *
 * The campaigns service hides drip-STEP campaigns from every find that does not
 * name `dripId`. That is right for a campaign LIST and fatal here: a drip step
 * is a real campaign this tick is supposed to start, so for a year the default
 * filter meant no client ever saw one and every drip step waited on the backend
 * cron instead. `onlyDripSteps` asks for the exact complement; the backend
 * resolves it to `{dripId: {$ne: null}}` (a query string cannot carry `null`,
 * and `$exists` is not whitelisted — see drip-step-scope.ts).
 *
 * Deliberately NOT one widened query: the campaigns reducer's `total` comes
 * from the last find, and management-list paginates on `total` vs a
 * drip-filtered length. Inflating it there would skip past real campaigns and
 * silently drop them from the list.
 */
export const getPendingAndRuningCampaigns = createAsyncThunk(
    "getPendingAndRuningCampaigns",
    async (_, { dispatch }) => {
        const pendingAndRunning = {
            campaignStatus: { $in: ["pending", "running"] },
            $limit: -1,
        };

        // Settled, not all: a drip-step fetch that fails must not stop standalone
        // campaigns from being dispatched, and vice versa.
        const [standalone, dripSteps] = await Promise.allSettled([
            dispatch(DBCampaignThunks.find(pendingAndRunning)).unwrap(),
            dispatch(DBCampaignThunks.find({ ...pendingAndRunning, onlyDripSteps: true })).unwrap(),
        ]);

        const campaigns = [
            ...(standalone.status === "fulfilled" ? standalone.value ?? [] : []),
            ...(dripSteps.status === "fulfilled" ? dripSteps.value ?? [] : []),
        ];

        // FE#341 — load launched drips DIRECTLY (not only those referenced by a fetched step
        // campaign). The campaigns service HIDES drip-step campaigns from finds that don't name
        // `dripId`, and the onlyDripSteps fetch above is best-effort — so relying on it to surface
        // drips left a future scheduled drip absent from DBDripReducer on the calendar, and the
        // calendar renders drips FROM the drip doc. This makes the drip docs present regardless.
        // Best-effort (never blocks the campaign load).
        try {
            await dispatch(DBDripThunks.find({ deleted: { $ne: true }, status: { $in: ['active', 'paused'] }, $limit: -1 })).unwrap();
        } catch { /* drip load is best-effort; the campaigns are already in the store */ }

        // BE#313 — also ensure the drip docs for any fetched drip-step campaigns are present (covers
        // a drip whose status is not active/paused but whose current step is a live campaign).
        const dripIds = dripIdsFromCampaigns(campaigns);
        if (dripIds.length) {
            // #803 — batches of <=20: a longer `$in` 500s on the backend's qs arrayLimit.
            await Promise.allSettled(chunkIds(dripIds).map((batch) =>
                dispatch(DBDripThunks.find({ _id: { $in: batch }, $limit: -1 })).unwrap(),
            )); // best-effort: the campaigns are already in the store
        }

        return campaigns;
    }
);





export const getCampaignMessagesAndLogs = createAsyncThunk<
    { campaign: any; campaignLogs: any; campaignMessages: any },
    string
>(
    "campaigns/getCampaignMessagesAndLogs",
    async (campaignId, { dispatch, getState }) => {

        // The campaign list has usually just fetched this entity; re-fetching it
        // per mounted progress indicator caused N duplicate /campaigns calls per
        // page load. Only fetch when it's genuinely missing from the store.
        const existingCampaign = campaignSelectors.selectById(getState() as any, campaignId);

        const [campaign, campaignLogs, campaignMessages] = await Promise.all([
            existingCampaign
                ? Promise.resolve([existingCampaign])
                : dispatch(DBCampaignThunks.find({ _id: campaignId })).unwrap(),
            dispatch(DBMessageLogThunks.find({ campaignId, $paginate: false })).unwrap(),
            dispatch(DBUserMessageThunks.find({ campaignId, $paginate: false })).unwrap(),
        ]);

        return {
            campaign,
            campaignLogs,
            campaignMessages
        }

    }
);


// getCampaignRealStatus moved to a pure, unit-tested module. Re-exported here so existing importers
// (`@common-reducers/CampaignThunks`) keep working unchanged.
export { getCampaignRealStatus } from '@common/services/campaign-status';



export const cleanMessageLogsFromStateExceptRunningCampaignsThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("campaigns/cleanMessageLogsFromStateExceptRunningCampaignsThunk",
    async (_, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state = thunkAPI.getState();
        const runningCampaigns = getRunningCampaigns(state);
        const runningCampaignIds = runningCampaigns.map(campaign => campaign._id);
        const idsToRemove = Object.values(state.DBMessageLogReducer.entities)
            .filter((log): log is NonNullable<typeof log> => log !== undefined && !runningCampaignIds.includes(log.campaignId))
            .map(log => log._id);

        await dispatch(messageLogReducerActions.removeMany(idsToRemove));
    }
);




export const onCampaignEventDropThunk = createAsyncThunk<
    {
        success: boolean;
        errorType?: 'not_found' | 'not_pending' | 'too_close' | 'no_change';
        campaignStatus?: string;
        minutesUntilDue?: number;
    },
    {
        campaignId: string;
        start: string | Date;
        view: 'month' | 'week' | 'day';
    },
    { state: CommonRootState }
>(
    "campaigns/onCampaignEventDrop",
    async ({ campaignId, start, view }, { dispatch, getState }) => {
        const state = getState();

        // Fetch the campaign from state
        const campaign = campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { _id: campaignId });

        if (!campaign) {
            console.log('[CAMPAIGN_DND] Campaign not found in state:', campaignId);
            return { success: false, errorType: 'not_found' };
        }

        // Only allow dragging of pending campaigns
        if (campaign.campaignStatus !== 'pending') {
            console.log('[CAMPAIGN_DND] Campaign is not pending, ignoring drag');
            return {
                success: false,
                errorType: 'not_pending',
                campaignStatus: campaign.campaignStatus
            };
        }

        // Safety check: prevent dragging campaigns due within 5 minutes
        const now = moment();
        const campaignDueDate = moment(campaign.schedule?.dueDate);
        const minutesUntilDue = campaignDueDate.diff(now, 'minutes');

        if (minutesUntilDue <= 5 && minutesUntilDue >= 0) {
            console.log('[CAMPAIGN_DND] Campaign is due within 5 minutes, preventing drag:', {
                campaignId,
                dueDate: campaign.schedule?.dueDate,
                minutesUntilDue
            });
            return {
                success: false,
                errorType: 'too_close',
                minutesUntilDue
            };
        }

        // Calculate dropDate based on view
        let dropDate: moment.Moment;

        if (view === 'month') {
            // Month view: preserve original time, change only date
            const originalMoment = moment.utc(campaign.schedule?.dueDate);
            dropDate = moment.utc(start).set({
                hour: originalMoment.hour(),
                minute: originalMoment.minute(),
                second: originalMoment.second()
            }).startOf("minute");
        } else {
            // Week/Day view: use full date+time from drop target
            dropDate = moment.utc(start).startOf("minute");
        }

        // Check if date actually changed
        if (dropDate.isSame(campaign.schedule?.dueDate)) {
            console.log('[CAMPAIGN_DND] Drop date same as original, ignoring');
            return { success: false, errorType: 'no_change' };
        }

        console.log('[CAMPAIGN_DND] Updating campaign schedule:', {
            campaignId: campaign._id,
            originalDate: campaign.schedule?.dueDate,
            newDate: dropDate.toISOString(),
            view
        });

        // Update campaign schedule
        dispatch(DBCampaignThunks.patch({
            entity: {
                _id: campaign._id,
                schedule: {
                    ...campaign.schedule,
                    dueDate: dropDate.toISOString()
                }
            }
        }));

        return { success: true };
    }
);

/**
 * Dragging one step of a drip sequence.
 *
 * A drip's steps are not independent dates: the sequence has a start and each
 * step carries the gap that follows it, so a step's position is the forward
 * walk of those gaps. Writing the dragged step's own due date — what the
 * calendar used to do for the first step, the only one that was draggable —
 * left the drip projecting its original run from the unchanged start while the
 * moved campaign projected a second one, and the whole sequence appeared twice.
 *
 * So the drop is re-expressed as a change to the SEQUENCE (see
 * `resolveDripStepDrop`): the first step moves the start, a later step changes
 * the gap that places it. `dueDate` on a materialized step campaign is patched
 * alongside, so the queued send agrees with the sequence it belongs to.
 */
export const onDripStepDropThunk = createAsyncThunk<
    { success: boolean; refusal?: DripDropRefusal | 'not-found' | 'step-started' },
    { dripId: string; stepIndex: number; targetMs: number; campaignId?: string },
    { state: CommonRootState }
>(
    "campaigns/onDripStepDrop",
    async ({ dripId, stepIndex, targetMs, campaignId }, { dispatch, getState }) => {
        const state = getState();
        const drip = dripsSelectOneObjectByQuery(state.DBDripReducer, { _id: dripId });
        if (!drip || !Array.isArray(drip.steps) || drip.steps.length === 0) {
            return { success: false, refusal: 'not-found' };
        }

        // The sequence starts where its FIRST step is actually queued — the
        // materialized step-0 campaign — falling back to the drip's own
        // schedule before anything has been materialized.
        // NOTE: the query selectors take the SLICE; `campaignSelectors.selectAll`
        // takes the root state, and passing the slice to it threw — which the
        // caller's `.catch` swallowed, so the drop silently did nothing.
        const stepZero = campaignsSelectOneObjectByQuery(
            state.DBCampaignReducer, { dripId, dripStepIndex: 0 },
        ) ?? campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { dripId });
        const startIso = stepZero?.schedule?.dueDate ?? drip.schedule?.dueDate;
        const startMs = startIso ? new Date(startIso as any).getTime() : NaN;
        if (Number.isNaN(startMs)) return { success: false, refusal: 'not-found' };

        // A step already in flight cannot be re-timed: its audience and per-contact
        // send times were frozen into Redis when it started.
        const draggedCampaign = campaignId
            ? campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { _id: campaignId })
            : undefined;
        if (draggedCampaign && draggedCampaign.campaignStatus !== 'pending') {
            return { success: false, refusal: 'step-started' };
        }

        const resolution = resolveDripStepDrop({
            startMs,
            steps: drip.steps as any,
            stepIndex,
            targetMs,
            isRecurringDrip: !!drip.schedule?.isRecurring,
        });

        if (resolution.kind === 'noop') return { success: true };
        if (resolution.kind === 'refused') return { success: false, refusal: resolution.reason };

        if (resolution.kind === 'move-sequence') {
            // A REPEATING sequence's runs come from its rule, so moving the start has
            // to move the rule's ANCHOR by the same delta — otherwise the rule goes on
            // emitting the old run dates while `dueDate` claims a different start, and
            // the calendar shows a sequence whose runs disagree with its own beginning.
            // (Same class of mis-seating as a recurring message left due at an instant
            // its rule no longer emits.)
            const existingSchedule: any = drip.schedule ?? {};
            const movedSchedule: any = { ...existingSchedule, dueDate: resolution.dueDate };
            if (existingSchedule.isRecurring && typeof existingSchedule.rruleset === 'string') {
                movedSchedule.rruleset = shiftRrulesetAnchor(existingSchedule.rruleset, resolution.shiftMs);
            }

            // Both halves, or the calendar disagrees with itself: the drip is what
            // projects the later steps, the campaign is what actually sends.
            await dispatch(DBDripThunks.patch({
                entity: { _id: dripId, schedule: movedSchedule } as any,
            }));
            if (stepZero?._id) {
                await dispatch(DBCampaignThunks.patch({
                    entity: { _id: stepZero._id, schedule: { ...(stepZero.schedule ?? {}), dueDate: resolution.dueDate } } as any,
                }));
            }
            return { success: true };
        }

        // set-gap: re-time this step by the gap that places it. Later steps keep
        // their own gaps and follow along.
        const steps = (drip.steps as any[]).map((s, i) =>
            i === resolution.stepIndex ? { ...s, gapAfter: resolution.gap } : s);
        await dispatch(DBDripThunks.patch({ entity: { _id: dripId, steps } as any }));
        if (draggedCampaign?._id) {
            await dispatch(DBCampaignThunks.patch({
                entity: { _id: draggedCampaign._id, schedule: { ...(draggedCampaign.schedule ?? {}), dueDate: resolution.dueDate } } as any,
            }));
        }
        return { success: true };
    }
);

export const findCmpaignAUdienceBoardsColumns = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "campaigns/findCmpaignAUdienceBoardsColumns",
    async (_, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state = thunkAPI.getState();
        const newCampaign = state.CampaignReducer.newCampaign
        for (let boardId of newCampaign?.boardIDArray ?? []) {
            dispatch(DBTaskColumnThunks.find({
                boardId,
                deleted: false,
                $paginate: false,
            }))
        }
    }
)

export const saveTemplateThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "campaigns/saveTemplateThunk",
    async (_, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state = thunkAPI.getState();

        const templateId = state.ScheduledMessageEditorReducer.templateId
        const templateName = state.ScheduledMessageEditorReducer.templateName
        const message = state.ScheduledMessageEditorReducer.message
        const asset = state.ScheduledMessageEditorReducer.asset ?? null;
        const imageId = state.ScheduledMessageEditorReducer.imageId
        const sendLinkPreview = state.ScheduledMessageEditorReducer.sendLinkPreview
        const linkPreview = state.ScheduledMessageEditorReducer.linkPreview
        const isPoll = state.ScheduledMessageEditorReducer.isPoll
        const poll = state.ScheduledMessageEditorReducer.poll

        let result: any

        // Mode-aware payload — a template is either a poll OR a text
        // message, never both. Without this the saved entity carries
        // stale data from a previous edit (e.g. switching from poll mode
        // back to text mode would still ship the old poll alongside the
        // new message).
        const pollPayload: Partial<Template> = {
            message: '',
            asset: undefined,
            imageId: '',
            sendLinkPreview: false,
            linkPreview: {},
            isPoll: true,
            poll,
        };
        const textPayload: Partial<Template> = {
            message,
            asset: asset ?? undefined,
            imageId,
            sendLinkPreview,
            linkPreview,
            isPoll: false,
            poll: {},
        };
        const modePayload = isPoll ? pollPayload : textPayload;

        if (templateId) {
            const template: Partial<Template> = {
                _id: templateId,
                name: templateName, // Read from ScheduledMessageEditorReducer - single source of truth
                ...modePayload,
            }
            result = await dispatch(DBTemplateThunks.patch({ entity: template }));

        } else {
            const template: Template = {
                name: templateName, // Read from ScheduledMessageEditorReducer - single source of truth
                ...modePayload,
            } as Template;
            result = await dispatch(DBTemplateThunks.create(template));
        }

        // Update currentTemplate with the updated template to keep wrapper in sync
        const updatedTemplate = result.payload as Template;
        dispatch(setCurrentTemplate(updatedTemplate));

        // Mark as saved and update original data
        dispatch(scheduledMessageEditorActions.markAsSaved());
        dispatch(scheduledMessageEditorActions.setOriginalTemplateData(updatedTemplate));
    }
)

export const saveAsNewTemplateThunk = createAsyncThunk<
    void,
    string, // templateName parameter
    { state: CommonRootState }
>(
    "campaigns/saveAsNewTemplateThunk",
    async (templateName: string, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const state = thunkAPI.getState();

        // Get current template data from Redux state
        const message = state.ScheduledMessageEditorReducer.message
        const asset = state.ScheduledMessageEditorReducer.asset ?? undefined;
        const imageId = state.ScheduledMessageEditorReducer.imageId
        const sendLinkPreview = state.ScheduledMessageEditorReducer.sendLinkPreview
        const linkPreview = state.ScheduledMessageEditorReducer.linkPreview
        const isPoll = state.ScheduledMessageEditorReducer.isPoll
        const poll = state.ScheduledMessageEditorReducer.poll

        // Reject empty templates. The check is mode-aware: a text template
        // needs non-blank message text; a poll template needs a question
        // AND at least two options with non-empty text. Otherwise we'd
        // either silently swallow the click (poll mode, the old behaviour
        // that looked like "nothing happens") or create a meaningless
        // blank template.
        const validOptionCount = Array.isArray(poll?.options)
            ? poll!.options!.filter((o: any) => typeof o?.text === 'string' && o.text.trim().length > 0).length
            : 0;
        const hasValidPoll = isPoll
            && typeof poll?.question === 'string'
            && (poll.question as string).trim().length > 0
            && validOptionCount >= 2;
        const hasValidMessage = !!message && message.trim().length > 0;

        if (isPoll ? !hasValidPoll : !hasValidMessage) {
            return;
        }

        // Mode-aware payload — never mix poll data into a text template
        // and never carry stale text into a poll template. Both used to
        // be shipped together regardless of mode, which made the saved
        // entity carry leftovers from a previous edit.
        const newTemplate: Template = isPoll
            ? ({
                name: templateName.trim(),
                message: '',
                asset: undefined,
                imageId: '',
                sendLinkPreview: false,
                linkPreview: {},
                isPoll: true,
                poll,
            } as Template)
            : ({
                name: templateName.trim(),
                message,
                asset,
                imageId,
                sendLinkPreview,
                linkPreview,
                isPoll: false,
                poll: {},
            } as Template);

        const result = await dispatch(DBTemplateThunks.create(newTemplate));

        // Update currentTemplate with the new template to keep wrapper in sync
        const createdTemplate = result.payload as Template;
        dispatch(setCurrentTemplate(createdTemplate));

        // Mark as saved and update original data
        dispatch(scheduledMessageEditorActions.markAsSaved());
        dispatch(scheduledMessageEditorActions.setOriginalTemplateData(createdTemplate));
    }
);

/**
 * BE#658 (4a): load delivery evidence for the campaign log view's rows WITHOUT touching the
 * userMessage entity adapter. The real affected shape is a before-wa-send-only log trail whose
 * embedded snapshot is pre-send, so the confirmed userMessage (the delivery proof) must come from
 * the store — but the log view never fetches userMessages. We fetch them SCOPED by `_id $in`
 * (indexed point-lookups), projected to the evidence fields (delivery acks + BE#710 failure-reason),
 * batched ≤200, and merge the result into CampaignReducer.deliveryEvidence (a dedicated map),
 * caching across opens.
 */
const EVIDENCE_BATCH = 200;
// BE#710: `sendFailureReason` + `sendFailureError` are part of the evidence projection because the
// not-on-WhatsApp classifier (isNotOnWhatsAppFailure, used by the status filter + the row error
// tooltip) reads them off `deliveryMessage ?? log.message`. The evidence doc is PREFERRED over the
// log's embedded snapshot when the full userMessage isn't in DBUserMessageReducer (the normal state
// of the log view), so a reason-less evidence doc made the "View these numbers" filter return 0
// rows and the row tooltip fall back to the generic "error occurred" copy.
const EVIDENCE_SELECT = ['status', 'serverAck', 'waMessageKey', 'acknowledged', 'sendFailureReason', 'sendFailureError'];
export const fetchCampaignDeliveryEvidenceThunk = createAsyncThunk<void, string[], { state: CommonRootState }>(
    "campaigns/fetchCampaignDeliveryEvidence",
    async (messageIds, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;
        const have = thunkAPI.getState().CampaignReducer.deliveryEvidence ?? {};
        // CACHE: only fetch ids we don't already have evidence for → reopening a campaign, or a new
        // log event that adds no new messageIds, fetches nothing.
        const missing = Array.from(new Set(messageIds.filter((id) => id && !have[id])));
        if (missing.length === 0) return;
        for (let i = 0; i < missing.length; i += EVIDENCE_BATCH) {
            const chunk = missing.slice(i, i + EVIDENCE_BATCH);
            // `_id $in` in the array-notation the backend-api expects; projected to evidence fields.
            const query: Record<string, unknown> = { '_id[$in][]': chunk, $select: EVIDENCE_SELECT, $paginate: false };
            const sub = dispatch(findEntity.initiate({ service: 'user-messages', query }, { forceRefetch: true }));
            try {
                const res: any = await sub.unwrap();
                const docs: any[] = Array.isArray(res) ? res : (res?.data ?? []);
                if (docs.length) dispatch(mergeDeliveryEvidence(docs.map((d) => ({
                    _id: String(d._id), status: d.status, serverAck: d.serverAck, waMessageKey: d.waMessageKey, acknowledged: d.acknowledged,
                    sendFailureReason: d.sendFailureReason, sendFailureError: d.sendFailureError,
                }))));
            } catch (e) {
                console.warn('[fetchCampaignDeliveryEvidence] batch failed (non-blocking)', e);
            } finally {
                sub.unsubscribe?.();
            }
        }
    }
);

// Bulk Dashboard Data Fetching Thunks
export const fetchCampaignForLogsViewThunk = createAsyncThunk<
    void,
    string, // campaignId
    { state: CommonRootState }
>(
    "campaigns/fetchCampaignForLogsViewThunk",
    async (campaignId, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;

        if (!campaignId) return;

        // In-app navigation (from the campaign list) already has the campaign in
        // the store — nothing to load.
        const already = campaignsSelectOneObjectByQuery(thunkAPI.getState().DBCampaignReducer, { _id: campaignId });
        if (already) return;

        // Cold deep-link / reload (e.g. pasting /campaigns/:id/logs): the campaigns
        // `find` service does NOT match a string `_id` (it isn't cast to an
        // ObjectId), so `find({ _id })` resolves EMPTY and the logs view renders
        // blank (BulkLogsView returns null without the campaign). The GET-by-id
        // endpoint DOES cast the id, so load the single campaign that way and
        // upsert it into the store.
        try {
            const state = thunkAPI.getState();
            let token = state.UserReducer?.accessToken;
            if (!token) {
                const { authService } = await import("@/common/bt-auth/auth-service");
                token = await authService.getPersistedToken();
            }
            const res = await fetch(`${appConfig.API_ENDPOINT}/campaigns/${campaignId}`, {
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            if (!res.ok) return;
            const campaign = (await res.json()) as Campaign;
            if (campaign?._id) dispatch(campaignReducerActions.upsertOne(campaign));
        } catch (err) {
            console.error("[fetchCampaignForLogsViewThunk] campaign get-by-id failed", err);
        }
    }
);

export const fetchCampaignStatsThunk = createAsyncThunk<
    number, // number of acks that ADVANCED this pass (0 = nothing changed) — lets pollers back off
    string, // campaignId
    { state: CommonRootState }
>(
    "campaigns/fetchCampaignStatsThunk",
    async (campaignId, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;

        if (!campaignId) return 0;

        // Import dynamically to avoid circular dependencies
        const { waGetMessagesAckAndCalculateStatisticsTool } = await import(/* webpackMode: "eager" */ "./CampaignLogsThunks");
        const result = await dispatch(waGetMessagesAckAndCalculateStatisticsTool(campaignId)).unwrap();
        return result?.recordedCount ?? 0;
    }
);

export const refreshCampaignLogsThunk = createAsyncThunk<
    void,
    string, // campaignId
    { state: CommonRootState }
>(
    "campaigns/refreshCampaignLogsThunk",
    async (campaignId, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;

        if (!campaignId) return;

        // Fetch campaign data and stats
        await dispatch(fetchCampaignForLogsViewThunk(campaignId));
        await dispatch(fetchCampaignStatsThunk(campaignId));
    }
)
